import { grey } from "@ant-design/colors"
import { faCloudDownload, faInbox, faRotate, faTags } from "@fortawesome/pro-solid-svg-icons"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { ConfigProvider, Empty, Flex, Input, List, Row, Skeleton, Space, theme, Typography } from "antd"
import { useEffect, useState } from "react"
import InfiniteScroll from "react-infinite-scroll-component"
import { hasValue } from "../../helpers/nullable"
import { useDebouncedState } from "../../hooks/useDebouncedState"
import { NavigationService } from "../../services/navigationService"
import LabeledSwitch from "../common/LabeledSwitch"
import PageSidebar from "../common/PageSidebar"
import SidebarCreateButton from "../common/SidebarCreateButton"
import VSpace from "../common/VSpace"
import { CustomerDto } from "./customerApi"
import { useCustomersQuery, useInvalidateCustomers } from "./customerQueries"
import { getValidCustomerId } from "./customersModel"
import './CustomersSidebar.css'

const { Text } = Typography

const PAGE_SIZE = 1000

type SidebarCustomers = {
  customers: CustomerDto[]
  fetchNextPage: () => void
  hasMore: boolean
  isLoading: boolean
}

const useCustomers = (
  search: string,
  isLead: boolean,
  isCustomer: boolean,
  updateTrigger: number,
  showDeleted: boolean
): SidebarCustomers => {
  const customersQuery = useCustomersQuery(search, PAGE_SIZE, isLead, isCustomer, updateTrigger, showDeleted);
  const { pages } = customersQuery.data ?? { pages: [] };

  return {
    customers: pages.flatMap((x) => x) ?? [],
    fetchNextPage: customersQuery.fetchNextPage,
    hasMore: pages[pages.length - 1]?.length === PAGE_SIZE,
    isLoading: customersQuery.isLoading || customersQuery.isFetchingNextPage,
  };
};

type Props = {
  onAdd: () => void
  onBeforeSelect: () => boolean
  onSelect: (customer: { id: number }) => void
  onImport: () => void
  updateTrigger: number
  navigationService: NavigationService
  showDeleted: boolean
  newCustomerId?: number
}

const CustomersSidebar = ({ onAdd, onBeforeSelect, onSelect, onImport, updateTrigger, navigationService, showDeleted, newCustomerId }: Props) => {
  const [isLead, setIsLead] = useState(true)
  const [isCustomer, setIsCustomer] = useState(true)

  const [selectedCustomerId, setSelectedCustomerId] = useState<number | null>(getValidCustomerId(navigationService.hash));
  const { debouncedState: debouncedSearch, setState: setDebouncedSearch, state: search } = useDebouncedState("", 300);
  const invalidateCustomers = useInvalidateCustomers()
  const { customers, fetchNextPage, hasMore, isLoading } = useCustomers(debouncedSearch, isLead, isCustomer, updateTrigger, showDeleted)

  useEffect(() => {
    if (hasValue(selectedCustomerId)) {
      onSelect({ id: selectedCustomerId })
    }
  }, [])

  useEffect(() => {
    if(hasValue(newCustomerId)) {
      setSelectedCustomerId(newCustomerId)
      setTimeout(() => {
        navigationService.setHash(`${newCustomerId}`)
      }, 0)
    }
  }, [newCustomerId])

  return <PageSidebar>
    <VSpace size='large'>
      <div>
        <VSpace>
          <VSpace style={{ padding: '10px 10px 0 10px' }}>
            <Row justify='space-between' align='middle'>
              <Text style={{ color: 'white', fontSize: 18, fontFamily: 'Open Sans, sans-serif' }}>
                {showDeleted ? 'Deleted Customers' : 'Customers'}
              </Text>
              <FontAwesomeIcon
                icon={faRotate}
                spin={isLoading}
                speed={30}
                style={{ cursor: 'pointer', color: grey[2] }}
                onClick={invalidateCustomers}
              />
            </Row>
            <Flex justify='space-between' style={{ color: "white", fontSize: 12, paddingRight: 20 }}>
              <LabeledSwitch label='Leads' size="small" value={isLead} onChange={e => setIsLead(e)} />
              <LabeledSwitch label='Customers' size="small" value={isCustomer} onChange={e => setIsCustomer(e)} />
            </Flex>
            <Row>
              <Input.Search
                placeholder='Search'
                value={search}
                onChange={e => setDebouncedSearch(e.target.value ?? '')}
                allowClear
              />
            </Row>
          </VSpace>
          {!showDeleted && <SidebarCreateButton text='Create New Client' onClick={() => {
            navigationService.setHash('')
            setSelectedCustomerId(null)
            onAdd()
          }} />}

          {!showDeleted && <div
            className={'sidebar-catalog-item'}
            onClick={() => onImport()}
          >
            <Text type='secondary' style={{ display: 'block', color: '#ffffffd9' }}>
              Import Customers
            </Text>
            <Text type="secondary" style={{ display: 'block', color: '#ffffff73' }}>
              <small>
                <Space>
                  <FontAwesomeIcon icon={faCloudDownload} />
                  Import customers from QuickBooks
                </Space>
              </small>
            </Text>
          </div>}
        </VSpace>
      </div>
    </VSpace>

    <ConfigProvider
      theme={{
        algorithm: theme.darkAlgorithm,
      }}
      renderEmpty={() =>
        <Empty
          description="No items found"
          image={<FontAwesomeIcon icon={faInbox} size="xl" />}
        />
      }
    >
      <div
        id="customers-sidebar-scrollable-div"
        className="react-scrollable-list"
        style={{
          height: `calc(100vh - 260px)`,
        }}
      >
        <InfiniteScroll
          dataLength={customers.length}
          next={fetchNextPage}
          hasMore={hasMore}
          loader={<Skeleton active />}
          scrollableTarget="customers-sidebar-scrollable-div"
          endMessage={<div style={{ padding: 10, textAlign: 'center' }}>
            <Text>No more items</Text>
          </div>}
        >
          <List
            dataSource={customers}
            pagination={false}
            loading={isLoading}
            renderItem={record => <Flex 
              vertical 
              className={`sidebar-customer ${selectedCustomerId === record.id ? 'selected' : ''}`}          
              onClick={() => {
                if (onBeforeSelect()) {
                  onSelect(record)
                  navigationService.setHash(record.url)
                  setSelectedCustomerId(record.id)
                }
              }}
            >
              <Text color="white">{record.displayName}</Text>
              <Text type="secondary" color={grey.primary} style={{ fontSize: 12 }}>{record.contact}</Text>
              {
                record.tags && <Text type="secondary" color={grey.primary} style={{ fontSize: 10 }}>
                  <FontAwesomeIcon icon={faTags} style={{ marginRight: 5 }} />
                  {record.tags}
                </Text>
              }
            </Flex>}
          />
        </InfiniteScroll>
      </div>
    </ConfigProvider>
  </PageSidebar>
}

export default CustomersSidebar